import PropTypes from "prop-types"
import React, { useContext, useEffect } from "react"
import { useQuery } from "react-query"

import Button from "src/components/Button"
import ReloadableWidget from "src/components/ReloadableWidget"

import { queryDockwaPlusDealsWidget } from "src/api/Dashboard"

import { useTracker } from "src/hooks/use_tracker"

import { DashboardContext } from "./DashboardView"
import FuelDealIcon from "./FuelDealIcon"
import LineChartIcon from "./LineChartIcon"
import ReservationDealIcon from "./ReservationDealIcon"

const refreshRate = 300 * 1000 // 5 minutes

const DockwaPlusDeals = ({ marinaSlug }) => {
  const { setShowDockwaPlusDeals } = useContext(DashboardContext)
  const tracker = useTracker()

  const { isLoading, isError, data } = useQuery(
    ["dockwaPlusDealsWidget", marinaSlug],
    () => queryDockwaPlusDealsWidget(marinaSlug),
    { retry: false, refetchInterval: refreshRate, refetchOnWindowFocus: false }
  )

  useEffect(() => {
    if (data) {
      setShowDockwaPlusDeals(
        data?.recommendedDockageDeal?.type === "dockage" ||
          data?.recommendedFuelDeal?.type === "fuel"
      )
    }
  }, [data, setShowDockwaPlusDeals])

  const hasActiveDeals =
    data?.recommendedDockageDeal?.type === "dockage" ||
    data?.recommendedFuelDeal?.type === "fuel"
  const recommendedDockageDeal = data?.recommendedDockageDeal
  const recommendedFuelDeal = data?.recommendedFuelDeal
  const viewsPercentageChangeDirection =
    data?.profileViewsPercentageChange > 0 ? "up" : "down"

  const trackAndLoadUrl = (event, url, params = {}) => {
    tracker.trackEvent(event, {
      marina_id: marinaSlug,
      experience_name: "dashboard",
      ...params,
    })
    window.open(url)
  }

  return (
    <ReloadableWidget isLoading={isLoading} isError={isError}>
      {hasActiveDeals && (
        <div className="card relative flex flex-col p-6">
          <div className="mb-6 flex items-center justify-between">
            <span className="text-start text-xl font-bold">
              Promote Your Marina
            </span>
          </div>
          {data.profileViewsLast60Days > 0 && (
            <div className="mb-4 flex w-full items-center rounded border border-gray-300 bg-gray-100 p-4">
              <div className="ml-2 flex flex-shrink-0 flex-col items-center justify-center">
                <LineChartIcon direction={viewsPercentageChangeDirection} />
                <span
                  className={`${
                    viewsPercentageChangeDirection === "up"
                      ? "text-teal-500"
                      : "text-red-500"
                  } font-bold`}
                >
                  {viewsPercentageChangeDirection === "up" && "+"}
                  {Math.round(data.profileViewsPercentageChange)}%
                </span>
              </div>
              <div className="ml-8 flex-grow">
                <p className="m-0 text-gray-700">
                  Your marina received {data.profileViewsLast60Days} profile
                  page views in the last 60 days. This is{" "}
                  {Math.abs(Math.round(data.profileViewsPercentageChange))}%{" "}
                  {viewsPercentageChangeDirection === "up" ? "higher" : "lower"}{" "}
                  than the same period last year.
                </p>
              </div>
            </div>
          )}
          <div className="mb-4 text-gray-700">
            Boost your marina to the top of Dockwa search and help your marina
            stand out by running a Dockwa Deal. Here&apos;s our recommendation:
          </div>
          <div className="flex w-full flex-col space-y-6">
            {recommendedDockageDeal && (
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center">
                    <ReservationDealIcon />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-base font-semibold">
                        Reservation Deal
                      </span>
                    </div>
                    <div className="text-gray-700">
                      {recommendedDockageDeal.subtitle}
                    </div>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={() =>
                    trackAndLoadUrl(
                      "marina:dashboard_card:run_this_deal",
                      recommendedDockageDeal.link,
                      {
                        deal_type: "dockage",
                      }
                    )
                  }
                  className="ml-4 whitespace-nowrap"
                >
                  Run this deal
                </Button>
              </div>
            )}

            {recommendedFuelDeal && (
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center">
                    <FuelDealIcon />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-base font-semibold">Fuel Deal</span>
                    </div>
                    <div className="text-gray-700">
                      {recommendedFuelDeal.subtitle}
                    </div>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={() =>
                    trackAndLoadUrl(
                      "marina:dashboard_card:run_this_deal",
                      recommendedFuelDeal.link,
                      {
                        deal_type: "fuel",
                      }
                    )
                  }
                  className="ml-4 whitespace-nowrap"
                >
                  Run this deal
                </Button>
              </div>
            )}
          </div>
          <div className="mt-6">
            <a
              className="font-semibold text-blue-700"
              onClick={() =>
                trackAndLoadUrl(
                  "marina:dashboard_card:view_details",
                  `/manage/${marinaSlug}/dockwa_deals`
                )
              }
            >
              View more details
            </a>
          </div>
        </div>
      )}
    </ReloadableWidget>
  )
}

DockwaPlusDeals.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
}

export default DockwaPlusDeals
